import { useState, useMemo, useEffect, useContext } from "react";
import { toast, ToastContainer } from "react-toastify";
import { request } from "../../../utils/api";
import { formatYYYYMMDD, showImage } from "../../../utils";
import { AuthContext } from "../../../auth/AuthContext";
import DataTable from "react-data-table-component";
import Loading from "../utilComponent/Loading";
import * as Yup from "yup";

const addRowSchema = Yup.object().shape({
  product_qty: Yup.number()
    .typeError("Quantity must be a number")
    .positive("Quantity must be greater than 0")
    .required("Quantity is required"),
  deposit: Yup.number()
    .typeError("Deposit must be a number")
    .min(0, "Deposit cannot be negative")
    .nullable(),
  payment: Yup.number()
    .typeError("Payment must be a number")
    .min(0, "Payment cannot be negative")
    .nullable(),
  recurring_type: Yup.string()
    .oneOf(["none", "weekly", "monthly"], "Invalid recurring type")
    .required("Recurring type is required"),
});

const InvoiceCard = ({
  date = "",
  id = "",
  data = null,
  pagination = { current_page: 1, per_page: 15, total: 0 },
  loading = false,
  onPageChange = () => {},
  onRowUpdate = () => {},
  onReload = () => {},
  isCallFrom = "",
}) => {
  const { user } = useContext(AuthContext);
  const [invoiceList, setInvoiceList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [savingRowId, setSavingRowId] = useState(null);
  const [errors, setErrors] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedUser, setSelectedUser] = useState(id || "");
  const [selectedDate, setSelectedDate] = useState("");
  const [newRow, setNewRow] = useState({
    product_id: "",
    product_qty: "",
    deposit: "",
    payment: "",
    notes: "",
    recurring_type: "none",
  });

  useEffect(() => {
    if (!data) return;

    const transformed = data.map((item) => {
      const hasProduct = !!item?.product;

      // Price from API or product
      const price = hasProduct
        ? (item.price ?? item.product?.final_price ?? 0)
        : 0;

      // Qty from API
      const qty = item.product_qty || 0;

      // Deposit from API
      const deposit = item.deposit ?? 0;

      // Payment: use API value only when it is greater than 0, otherwise fallback
      const apiPayment = Number(item?.payment);
      const hasApiPayment = Number.isFinite(apiPayment) && apiPayment > 0;
      const payment = hasApiPayment
        ? apiPayment
        : hasProduct
          ? Number(price) * Number(qty)
          : 0;

      // Pay Amount Calculation
      const amountNeedToPay = hasProduct ? price * qty : deposit;

      // Balance can be negative when payment is greater than amount
      const balance = amountNeedToPay - payment;

      return {
        id: item?.id,
        date: formatYYYYMMDD(item?.created_at),

        hasProduct,
        product_id: item?.product?.id,
        user_id: item?.user?.id,

        label_name: item?.label_name,
        label_extract_no: item?.label_extract_no,

        product_qty: qty,
        status: item?.status,
        notes: item?.notes || item?.detail ,

        price,
        deposit,

        // Payment comes directly from API
        payment,

        // Calculated fields
        amountNeedToPay,
        balance,

        product_name: item?.product?.name ?? "",
        product_sku: item?.product?.sku ?? "",
        product_image: item?.product?.images?.[0]?.url,

        edit_access_request: item?.edit_access_request,
        isEdit: item?.isEdit
      };
    });

    setInvoiceList(transformed);
  }, [data]);

  const updateField = (rowId, key, value, manualEdit = false) => {
    onRowUpdate(rowId, key, value, null, manualEdit);
  };

  const filteredInvoiceList = useMemo(() => {
    if (!invoiceList) return [];
    return invoiceList.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    );
  }, [invoiceList, searchText]);

  const isMember = user?.role_key === "member";
  const isSuperAdminAndIsAdmin =
    user?.role_key === "super_admin" || user?.role_key === "admin";
  const isPrep = user?.role_key === "prep_team";

  const saveRow = async (row, isCallForEdit, approved) => {
    await saveRowData(row, isCallForEdit, approved);
  };

  const requestEditColumn = async (row) => {
    if (row.isEdit === true && row.edit_access_request === 1) {
      saveRow(row, true, "approved");
    } else {
      saveRowData(row, true);
    }
  };

  const saveRowData = async (row, isCallForEdit = false, approved = "") => {
    const fd = new FormData();
      // Member saving after approval
    if (row.isEdit === true && user?.role_key === "member") {
      fd.append("edit_access_request", 0);
      fd.append("is_edit", false);
    }

    // Member requesting edit
    else if (isCallForEdit) {
      fd.append("edit_access_request", 1);
      fd.append("is_edit", false);
    }

    // Admin approving request
    else if (approved === "approved") {
      fd.append("edit_access_request", 1);
      fd.append("is_edit", true);
    }

    // Default
    else {
      fd.append("edit_access_request", 0);
      fd.append("is_edit", false);
    }
    fd.append("product_id", row.product_id);
    fd.append("user_id", row.user_id);
    fd.append("label_name", row.label_name);
    fd.append("label_extract_no", row.label_extract_no);
    fd.append("product_qty", row.product_qty);
    fd.append("status", row.status);
    fd.append("approved", 1);
    fd.append("price", row.price);
    fd.append("deposit", row.deposit);
    fd.append("payment", row.payment);

    let url = "";
    if (!row.product_id) {
      fd.append("detail", row.notes);
      url = `invoice-summaries/${row.id}`;
    } else {
      fd.append("notes", row.notes);
      url = `labels/${row.id}`;
    }
    try {
      setSavingRowId(row.id);
      await request({
        url: url,
        method: "POST",
        data: fd,
      });

      toast.success("Row updated successfully", { autoClose: 3000 });
      onRowUpdate(row.id, null, null, fd);
      onReload();
    } catch (err) {
      toast.error("Failed to update row", { autoClose: 3000 });
    } finally {
      setSavingRowId(null);
    }
  };

  const checkEditable = (row) => {
    // Member cannot edit manual rows (rows without product)
    if (!row.hasProduct) return isMember;

    if (isSuperAdminAndIsAdmin) return false;

    // Member can edit only with permission
    if (isMember) return row.isEdit  !== true;

    // Everyone else cannot edit
    return true;
  };

  // const checkDepositEditable = (row) => {
  //   // Deposit field should always be editable for admins
  //   if (isSuperAdminAndIsAdmin) {
  //     return false; // false means editable (readOnly=false)
  //   }
  //   // For members, deposit is editable only when they have edit permission (is_edit === true)
  //   // else if (isMember) {
  //   //   return row.is_edit === true ? true : false; // Editable when is_edit is true, read-only when false
  //   // }
  // };

  const columns = [
    { name: "Date", selector: (row) => row.date },
    {
      name: "Product",
      width: "300px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2 py-2">
          {row.hasProduct && row.product_image ? (
            <img
              src={showImage(row.product_image)}
              alt=""
              width="80"
              height="70"
              className="rounded"
            />
          ) : (
            <div
              className="rounded bg-light d-flex align-items-center justify-content-center text-muted"
              style={{ width: 80, height: 70 }}
            >
              —
            </div>
          )}
          <div>
            <div className="fw-semibold">{row.product_name || "—"}</div>
            <small className="text-muted">{row.product_sku || ""}</small>
          </div>
        </div>
      ),
    },
    {
      name: "Price",
      cell: (row) => (
        <input
          type="number"
          className="form-control form-control-sm"
          value={row.price}
          readOnly={checkEditable(row)}
          disabled={!row.hasProduct}
          onChange={(e) => updateField(row.id, "price", Number(e.target.value))}
        />
      ),
    },
    {
      name: "Qty",
      cell: (row) => (
        <input
          type="number"
          className="form-control form-control-sm"
          value={row.product_qty}
          disabled={!row.hasProduct}
          readOnly={checkEditable(row)}
          onChange={(e) =>
            updateField(row.id, "product_qty", Number(e.target.value))
          }
        />
      ),
    },
    !isPrep && {
      name: "Deposit",
      cell: (row) => {
        if (row.hasProduct) return <span>----</span>;
        return (
          <input
            type="number"
            className="form-control form-control-sm"
            value={row.deposit}
            readOnly={!isSuperAdminAndIsAdmin}
            onChange={(e) => {
              updateField(row.id, "deposit", Number(e.target.value), true);
            }}
          />
        );
      },
    },
    !isPrep && {
      name: "Payment",
      cell: (row) => (
        <input
          type="number"
          className="form-control form-control-sm"
          value={row.payment}
          readOnly={checkEditable(row)}
          onChange={(e) =>
            updateField(row.id, "payment", Number(e.target.value), true)
          }
        />
      ),
    },
    // {
    //   name: "Pay Amount",
    //   cell: (row) => (
    //     <div>
    //       {row.hasProduct ? (
    //         <div className="fw-semibold">{row.amountNeedToPay || "—"}</div>
    //       ) : (
    //         <div>---</div>
    //       )}
    //     </div>
    //   ),
    // },
    !isPrep && { name: "Balance", selector: (row) => `$${row.balance}` },
    {
      name: "Note",
      cell: (row) => (
        <input
          type="text"
          className="form-control form-control-sm"
          rows="2"
          value={row.notes || ""}
          readOnly={checkEditable(row)}
          onChange={(e) => updateField(row.id, "notes", e.target.value, true)}
        />
      ),
    },
    {
      name: "Action",
      cell: (row) => {
        const canShowSave = !row.hasProduct || isSuperAdminAndIsAdmin;

        if (!canShowSave) return null;

        return (
          <button
            className="btn btn-sm btn-success"
            onClick={() => saveRow(row)}
            disabled={savingRowId === row.id}
          >
            {savingRowId === row.id ? "Saving..." : "Save"}
          </button>
        );
      },
    },
    isSuperAdminAndIsAdmin && {
      name: "Aprroved",
      cell: (row) =>
        row.edit_access_request ? (
          <button
            className="btn btn-sm btn-success"
            onClick={() => saveRow(row, true, "approved")}
            disabled={savingRowId === row.id}
          >
            {savingRowId === row.id ? "Approved..." : "Approved"}
          </button>
        ) : (
          <span>No Request</span>
        ),
    },
    isMember && {
      name: "Request",
      cell: (row) => {
        if (!row.hasProduct) return null;

        const isSaving = savingRowId === row.id;
        const isAlreadyRequested = row.edit_access_request;
        const canSave = row.isEdit === true;
        const isWaitingApproval = row.isEdit === false && isAlreadyRequested;
        const buttonClass = isWaitingApproval ? "btn-secondary" : "btn-success";
        const buttonLabel = isSaving
          ? "Requesting Edit..."
          : canSave
            ? "Save"
            : isAlreadyRequested
              ? "Waiting for Approved"
              : "Request Edit";

        return (
          <button
            className={`btn btn-sm ${buttonClass}`}
            onClick={() => requestEditColumn(row)}
            disabled={isSaving || isWaitingApproval}
          >
            {buttonLabel}
          </button>
        );
      },
    },
  ].filter(Boolean);

  const openAddCalculationRowModal = () => {
    setShowAddModal(true);
    // getAssignProductList();
  };

  const closeAddCalculationRowModal = () => {
    setShowAddModal(false);
    setNewRow({
      product_id: "",
      product_qty: "",
      payment: "",
      deposit: "",
      notes: "",
      recurring_type: "none",
    });
  };

  const handleNewRowChange = (e) => {
    const { name, value } = e.target;
    if (name === "product_id") {
      // transformDataForDropDown returns items with { value, label, price }
      const product = products.find((p) => p.value == value);
      setNewRow((prev) => ({
        ...prev,
        product_id: value,
        price: product?.price || "",
      }));
      return;
    }

    setNewRow((prev) => ({ ...prev, [name]: value }));
  };

  const addCalculationRow = async () => {
    try {
      setErrors({});
      const payload = {
        ...newRow,
        recurring_type: newRow.recurring_type || "none",
      };

      await addRowSchema.validate(payload, { abortEarly: false });
      setAdding(true);
      const fd = new FormData();
      // Object.entries(newRow).forEach(([key, val]) => fd.append(key, val));
      // make sure recurring_type is sent explicitly
      fd.append("recurring_type", payload.recurring_type);
      fd.append("user_id", selectedUser);
      fd.append("deposit", newRow.deposit || 0);
      fd.append("payment", newRow.payment || 0);
      fd.append("detail", newRow.notes || "");
      fd.append("product_qty", newRow.product_qty || 0);
      fd.append("date", selectedDate || date);
      await request({
        url: "invoice-summaries",
        method: "POST",
        data: fd,
      });

      toast.success("Row added successfully", { autoClose: 3000 });
      closeAddCalculationRowModal();
      onRowUpdate();
    } catch (error) {
      if (error.name === "ValidationError") {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
        return;
      }

      toast.error("Failed to add row", { autoClose: 3000 });
    } finally {
      setAdding(false);
    }
  };

  // const getAssignProductList = async () => {
  //   try {
  //     setAssignProductLoading(true);

  //     const response = await request({
  //       url: `assign-products`,
  //       method: "GET",
  //     });

  //     if (response.status && response.data) {
  //       setProducts(transformDataForDropDown(response.data.items));
  //     }
  //   } catch (error) {
  //     console.error(error);
  //   } finally {
  //     setAssignProductLoading(false);
  //   }
  // };

  // const transformDataForDropDown = (items = []) => {
  //   const products = [];
  //   items?.forEach((item) => {
  //     if (Array.isArray(item.assigned_products)) {
  //       item.assigned_products.forEach((product) => {
  //         products.push({
  //           value: product.id,
  //           label: `${product.name} (${product.sku})`,
  //           price: product.final_price || 0,
  //         });
  //       });
  //     }
  //   });
  //   return products;
  // };

  return (
    <div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {isCallFrom !== "warehouseInvoice" && (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <input
            className="form-control w-50"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary"
              onClick={openAddCalculationRowModal}
            >
              Add
            </button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={filteredInvoiceList}
        pagination
        paginationServer
        paginationPerPage={pagination.per_page}
        paginationRowsPerPageOptions={[pagination.per_page, 25, 50]}
        paginationTotalRows={pagination.total}
        paginationDefaultPage={pagination.current_page}
        onChangePage={(page) => onPageChange(page)}
        onChangeRowsPerPage={(newPerPage, page) =>
          onPageChange(page, newPerPage)
        }
        progressPending={loading}
        progressComponent={<Loading />}
        highlightOnHover
      />
      {showAddModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>Add Invoice Row</h5>
                  <button
                    className="btn-close"
                    onClick={closeAddCalculationRowModal}
                  />
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label>Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                    </div>

                    {/* <div className="col-md-12">
                      <label>Product</label>
                      <select
                        className={`form-select ${errors.product_id ? "is-invalid" : ""}`}
                        name="product_id"
                        value={newRow.product_id}
                        onChange={handleNewRowChange}
                      >
                        <option value="">
                          {assignProductLoading ? "Loading..." : "~~Select Product~~"}
                        </option>

                        {Array.isArray(products) && products.length === 0 && !assignProductLoading && (
                          <option disabled>No Product Found</option>
                        )}

                        {Array.isArray(products) &&
                          products.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                      </select>
                    </div> */}
                    <div className="col-md-12">
                      <label>Qty</label>
                      <input
                        type="number"
                        className="form-control"
                        name="product_qty"
                        value={newRow.product_qty}
                        onChange={handleNewRowChange}
                      />
                      {errors.product_qty && (
                        <div className="invalid-feedback">
                          {errors.product_qty}
                        </div>
                      )}
                    </div>
                    <div className="col-md-12">
                      <label>Deposit</label>
                      <input
                        type="number"
                        className="form-control"
                        name="deposit"
                        value={newRow.deposit}
                        onChange={handleNewRowChange}
                      />
                      {errors.deposit && (
                        <div className="invalid-feedback">{errors.deposit}</div>
                      )}
                    </div>
                    <div className="col-md-12">
                      <label>Payment</label>
                      <input
                        type="number"
                        className="form-control"
                        name="payment"
                        value={newRow.payment}
                        onChange={handleNewRowChange}
                      />
                      {errors.payment && (
                        <div className="invalid-feedback">{errors.payment}</div>
                      )}
                    </div>

                    <div className="col-12">
                      <label>Detail</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        name="notes"
                        value={newRow.notes}
                        onChange={handleNewRowChange}
                      />
                    </div>

                    <div className="mb-2">
                      <label>Recurring</label>
                      <select
                        className={`form-control ${errors.recurring_type ? "is-invalid" : ""}`}
                        name="recurring_type"
                        value={newRow.recurring_type}
                        onChange={handleNewRowChange}
                      >
                        <option value="none">None</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                      {errors.recurring_type && (
                        <div className="invalid-feedback">
                          {errors.recurring_type}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={closeAddCalculationRowModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={addCalculationRow}
                    disabled={adding}
                  >
                    {adding ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
};

export default InvoiceCard;
